// PrimeVue Aura preset tuned to the PrintHQ design system (PrintHQ green,
// slate surfaces). Colors point at the CSS tokens in styles/main.css, which
// already switch for .light and .high-contrast, so both PrimeVue color
// schemes share them and every theme stays in step.
import Aura from '@primeuix/themes/aura'
import { definePreset } from '@primeuix/themes'

const floating = { background: 'var(--elevated)', borderColor: 'var(--border-strong)', color: 'var(--text)' }

// Soft tone: tinted fill and tone text, as in the design system's badges and callouts.
const soft = (tone: string) => ({
	background: `var(--${tone}-soft)`,
	hoverBackground: `var(--${tone}-soft)`,
	activeBackground: `var(--${tone}-soft)`,
	borderColor: 'transparent',
	hoverBorderColor: `color-mix(in srgb, var(--${tone}) 30%, transparent)`,
	activeBorderColor: `color-mix(in srgb, var(--${tone}) 30%, transparent)`,
	color: `var(--${tone}-text)`,
	hoverColor: `var(--${tone}-text)`,
	activeColor: `var(--${tone}-text)`,
	focusRing: { color: 'var(--focus)', shadow: 'none' },
})

const quiet = (color: string) => ({ hoverBackground: 'var(--card-hover)', activeBackground: 'var(--card-hover)', color })
const tinted = (tone: string) => ({ hoverBackground: `var(--${tone}-soft)`, activeBackground: `var(--${tone}-soft)`, color: `var(--${tone}-text)` })
const outlined = (tone: string) => ({ ...tinted(tone), borderColor: `color-mix(in srgb, var(--${tone}) 30%, transparent)` })

// Toasts and messages: an elevated card with the tone carried by the icon and summary.
const notice = (tone: string) => ({
	background: 'var(--elevated)',
	borderColor: 'var(--border-strong)',
	color: `var(--${tone}-text)`,
	detailColor: 'var(--text-2)',
	shadow: 'var(--shadow-lg)',
	closeButton: { hoverBackground: 'var(--card-hover)', focusRing: { color: 'var(--focus)', shadow: 'none' } },
	outlined: { color: `var(--${tone}-text)`, borderColor: `color-mix(in srgb, var(--${tone}) 30%, transparent)` },
	simple: { color: `var(--${tone}-text)` },
})
const callout = (tone: string) => ({
	...notice(tone),
	background: `var(--${tone}-soft)`,
	borderColor: `color-mix(in srgb, var(--${tone}) 30%, transparent)`,
	shadow: 'none',
})
const neutralNotice = {
	...notice('accent'),
	color: 'var(--text)',
	outlined: { color: 'var(--text)', borderColor: 'var(--border-strong)' },
	simple: { color: 'var(--text-2)' },
}
const neutralCallout = { ...neutralNotice, background: 'var(--card)', borderColor: 'var(--border)', shadow: 'none' }

const scheme = (surface: Record<string, string>) => ({
	surface,
	primary: {
		color: 'var(--accent)',
		contrastColor: 'var(--on-accent)',
		hoverColor: 'color-mix(in srgb, var(--accent), white 10%)',
		activeColor: 'var(--accent-strong)',
	},
	highlight: {
		background: 'var(--accent-soft)',
		focusBackground: 'var(--accent-soft)',
		color: 'var(--accent-text)',
		focusColor: 'var(--accent-text)',
	},
	mask: { background: 'rgba(0, 0, 0, 0.4)', color: 'var(--text)' },
	formField: {
		background: 'var(--well)',
		disabledBackground: 'var(--well)',
		filledBackground: 'var(--well)',
		filledHoverBackground: 'var(--well)',
		filledFocusBackground: 'var(--well)',
		borderColor: 'var(--border)',
		hoverBorderColor: 'var(--border-strong)',
		focusBorderColor: 'var(--accent)',
		invalidBorderColor: 'var(--danger)',
		color: 'var(--text)',
		disabledColor: 'var(--text-3)',
		placeholderColor: 'var(--text-3)',
		invalidPlaceholderColor: 'var(--danger-text)',
		floatLabelColor: 'var(--text-3)',
		floatLabelFocusColor: 'var(--accent-text)',
		floatLabelActiveColor: 'var(--text-3)',
		floatLabelInvalidColor: 'var(--danger-text)',
		iconColor: 'var(--text-3)',
		shadow: 'none',
	},
	text: { color: 'var(--text)', hoverColor: 'var(--text)', mutedColor: 'var(--text-2)', hoverMutedColor: 'var(--text)' },
	content: {
		background: 'var(--panel)',
		hoverBackground: 'var(--card-hover)',
		borderColor: 'var(--border)',
		color: 'var(--text)',
		hoverColor: 'var(--text)',
	},
	overlay: { select: floating, popover: floating, modal: floating },
	list: {
		option: {
			focusBackground: 'var(--menu-hover)',
			selectedBackground: 'var(--accent-soft)',
			selectedFocusBackground: 'var(--accent-soft)',
			color: 'var(--text)',
			focusColor: 'var(--text)',
			selectedColor: 'var(--accent-text)',
			selectedFocusColor: 'var(--accent-text)',
			icon: { color: 'var(--text-3)', focusColor: 'var(--text-2)' },
		},
		optionGroup: { background: 'transparent', color: 'var(--text-3)' },
	},
	navigation: {
		item: {
			focusBackground: 'var(--menu-hover)',
			activeBackground: 'var(--menu-hover)',
			color: 'var(--text)',
			focusColor: 'var(--text)',
			activeColor: 'var(--text)',
			icon: { color: 'var(--text-2)', focusColor: 'var(--text)', activeColor: 'var(--text)' },
		},
		submenuLabel: { background: 'transparent', color: 'var(--text-3)' },
		submenuIcon: { color: 'var(--text-3)', focusColor: 'var(--text-2)', activeColor: 'var(--text-2)' },
	},
})

const buttonScheme = {
	root: {
		primary: {
			background: 'var(--accent)',
			hoverBackground: 'color-mix(in srgb, var(--accent), white 10%)',
			activeBackground: 'var(--accent-strong)',
			borderColor: 'var(--accent)',
			hoverBorderColor: 'color-mix(in srgb, var(--accent), white 10%)',
			activeBorderColor: 'var(--accent-strong)',
			color: 'var(--on-accent)',
			hoverColor: 'var(--on-accent)',
			activeColor: 'var(--on-accent)',
			focusRing: { color: 'var(--focus)', shadow: 'none' },
		},
		// Secondary is the design system's subtle button.
		secondary: {
			background: 'var(--elevated)',
			hoverBackground: 'var(--card-hover)',
			activeBackground: 'var(--card-hover)',
			borderColor: 'var(--border)',
			hoverBorderColor: 'var(--border-strong)',
			activeBorderColor: 'var(--border-strong)',
			color: 'var(--text)',
			hoverColor: 'var(--text)',
			activeColor: 'var(--text)',
			focusRing: { color: 'var(--focus)', shadow: 'none' },
		},
		success: soft('accent'),
		info: soft('info'),
		warn: soft('warn'),
		help: soft('violet'),
		// Danger rests soft and turns solid on hover.
		danger: {
			...soft('danger'),
			hoverBackground: 'var(--danger-strong)',
			activeBackground: 'var(--danger-strong)',
			hoverBorderColor: 'var(--danger-strong)',
			activeBorderColor: 'var(--danger-strong)',
			hoverColor: 'var(--on-danger)',
			activeColor: 'var(--on-danger)',
		},
		contrast: {
			background: 'var(--text)',
			hoverBackground: 'var(--text)',
			activeBackground: 'var(--text)',
			borderColor: 'var(--text)',
			hoverBorderColor: 'var(--text)',
			activeBorderColor: 'var(--text)',
			color: 'var(--bg)',
			hoverColor: 'var(--bg)',
			activeColor: 'var(--bg)',
			focusRing: { color: 'var(--focus)', shadow: 'none' },
		},
	},
	outlined: {
		primary: outlined('accent'),
		secondary: { ...quiet('var(--text-2)'), borderColor: 'var(--border-strong)' },
		success: outlined('accent'),
		info: outlined('info'),
		warn: outlined('warn'),
		help: outlined('violet'),
		danger: outlined('danger'),
		contrast: { ...quiet('var(--text)'), borderColor: 'var(--border-strong)' },
		plain: { ...quiet('var(--text-2)'), borderColor: 'var(--border)' },
	},
	// Text buttons are the design system's ghost buttons.
	text: {
		primary: tinted('accent'),
		secondary: quiet('var(--text-2)'),
		success: tinted('accent'),
		info: tinted('info'),
		warn: tinted('warn'),
		help: tinted('violet'),
		danger: tinted('danger'),
		contrast: quiet('var(--text)'),
		plain: quiet('var(--text-2)'),
	},
	link: { color: 'var(--accent-text)', hoverColor: 'var(--accent-text)', activeColor: 'var(--accent-text)' },
}

const tone = (name: string) => ({ background: `var(--${name}-soft)`, color: `var(--${name}-text)` })
const neutralTone = { background: 'var(--elevated)', color: 'var(--text-2)' }

export const printhqPreset = definePreset(Aura, {
	semantic: {
		primary: {
			50: '#f0fdf4',
			100: '#dcfce7',
			200: '#bbf7d0',
			300: '#86efac',
			400: '#4ade80',
			500: '#22c55e',
			600: '#16a34a',
			700: '#15803d',
			800: '#166534',
			900: '#14532d',
			950: '#052e16',
		},
		transitionDuration: '150ms',
		focusRing: { width: '2px', style: 'solid', color: 'var(--focus)', offset: '2px', shadow: 'none' },
		disabledOpacity: '0.45',
		iconSize: '1rem',
		formField: {
			paddingX: '10px',
			paddingY: '7px',
			sm: { fontSize: '12px', paddingX: '8px', paddingY: '5px' },
			lg: { fontSize: '14px', paddingX: '12px', paddingY: '9px' },
			borderRadius: '8px',
			focusRing: { width: '0', style: 'none', color: 'transparent', offset: '0', shadow: 'var(--shadow-input-focus)' },
			transitionDuration: '150ms',
		},
		list: {
			padding: '4px',
			gap: '1px',
			header: { padding: '6px 10px 4px' },
			option: { padding: '6px 10px', borderRadius: '6px' },
			optionGroup: { padding: '6px 10px', fontWeight: '600' },
		},
		content: { borderRadius: '6px' },
		navigation: {
			list: { padding: '4px', gap: '1px' },
			item: { padding: '6px 10px', borderRadius: '6px', gap: '8px' },
			submenuLabel: { padding: '6px 10px', fontWeight: '600' },
			submenuIcon: { size: '0.875rem' },
		},
		overlay: {
			select: { borderRadius: '10px', shadow: 'var(--shadow-lg)' },
			popover: { borderRadius: '10px', padding: '12px', shadow: 'var(--shadow-lg)' },
			modal: { borderRadius: '14px', padding: '20px', shadow: 'var(--shadow-lg)' },
			navigation: { shadow: 'var(--shadow-lg)' },
		},
		colorScheme: {
			light: scheme({
				0: '#ffffff',
				50: '{slate.50}',
				100: '{slate.100}',
				200: '{slate.200}',
				300: '{slate.300}',
				400: '{slate.400}',
				500: '{slate.500}',
				600: '{slate.600}',
				700: '{slate.700}',
				800: '{slate.800}',
				900: '{slate.900}',
				950: '{slate.950}',
			}),
			dark: scheme({
				0: '#ffffff',
				50: '#e7edf4',
				100: '#cbd5e1',
				200: '#a3b0c0',
				300: '#8190a2',
				400: '#6f7d8e',
				500: '#4b5767',
				600: '#2a3441',
				700: '#1b2430',
				800: '#141b24',
				900: '#10161e',
				950: '#0b0f14',
			}),
		},
	},
	components: {
		button: {
			root: {
				borderRadius: '8px',
				gap: '6px',
				paddingX: '12px',
				paddingY: '7px',
				iconOnlyWidth: '32px',
				sm: { fontSize: '12px', paddingX: '10px', paddingY: '5px', iconOnlyWidth: '28px' },
				lg: { fontSize: '14px', paddingX: '16px', paddingY: '11px', iconOnlyWidth: '40px' },
				label: { fontWeight: '500' },
				raisedShadow: 'var(--shadow)',
			},
			colorScheme: { light: buttonScheme, dark: buttonScheme },
		},
		toast: {
			root: { width: '25rem', borderRadius: '6px', borderWidth: '1px' },
			icon: { size: '1.125rem' },
			content: { padding: '12px 14px', gap: '8px' },
			text: { gap: '4px' },
			summary: { fontWeight: '500', fontSize: '1rem' },
			detail: { fontWeight: '500', fontSize: '0.875rem' },
			colorScheme: {
				light: { root: { blur: '0' }, info: notice('info'), success: notice('accent'), warn: notice('warn'), error: notice('danger'), secondary: neutralNotice, contrast: neutralNotice },
				dark: { root: { blur: '0' }, info: notice('info'), success: notice('accent'), warn: notice('warn'), error: notice('danger'), secondary: neutralNotice, contrast: neutralNotice },
			},
		},
		message: {
			root: { borderRadius: '8px' },
			content: { padding: '8px 12px', gap: '8px' },
			text: { fontSize: '13px', fontWeight: '500', sm: { fontSize: '12px' }, lg: { fontSize: '14px' } },
			colorScheme: {
				light: { info: callout('info'), success: callout('accent'), warn: callout('warn'), error: callout('danger'), secondary: neutralCallout, contrast: neutralCallout },
				dark: { info: callout('info'), success: callout('accent'), warn: callout('warn'), error: callout('danger'), secondary: neutralCallout, contrast: neutralCallout },
			},
		},
		tag: {
			root: { fontSize: '11px', fontWeight: '500', padding: '2px 6px', gap: '4px', borderRadius: '6px', roundedBorderRadius: '9999px' },
			icon: { size: '11px' },
			colorScheme: {
				light: { primary: tone('accent'), secondary: neutralTone, success: tone('accent'), info: tone('info'), warn: tone('warn'), danger: tone('danger'), contrast: { background: 'var(--text)', color: 'var(--bg)' } },
				dark: { primary: tone('accent'), secondary: neutralTone, success: tone('accent'), info: tone('info'), warn: tone('warn'), danger: tone('danger'), contrast: { background: 'var(--text)', color: 'var(--bg)' } },
			},
		},
		badge: {
			root: { borderRadius: '6px', padding: '0 6px', fontSize: '11px', fontWeight: '600', minWidth: '20px', height: '20px' },
			sm: { fontSize: '10px', minWidth: '16px', height: '16px' },
			colorScheme: {
				light: { primary: { background: 'var(--accent)', color: 'var(--on-accent)' }, secondary: neutralTone, success: tone('accent'), info: tone('info'), warn: tone('warn'), danger: tone('danger'), contrast: { background: 'var(--text)', color: 'var(--bg)' } },
				dark: { primary: { background: 'var(--accent)', color: 'var(--on-accent)' }, secondary: neutralTone, success: tone('accent'), info: tone('info'), warn: tone('warn'), danger: tone('danger'), contrast: { background: 'var(--text)', color: 'var(--bg)' } },
			},
		},
		toggleswitch: {
			root: { width: '36px', height: '20px', borderRadius: '9999px', gap: '2px', shadow: 'none', slideDuration: '150ms' },
			handle: { borderRadius: '50%', size: '16px' },
			colorScheme: {
				light: {
					root: { background: 'var(--border-strong)', hoverBackground: 'var(--border-strong)', checkedBackground: 'var(--accent)', checkedHoverBackground: 'var(--accent)' },
					handle: { background: '#ffffff', hoverBackground: '#ffffff', checkedBackground: '#ffffff', checkedHoverBackground: '#ffffff', color: 'var(--text-3)', hoverColor: 'var(--text-2)', checkedColor: 'var(--accent)', checkedHoverColor: 'var(--accent)' },
				},
				dark: {
					root: { background: 'var(--border-strong)', hoverBackground: 'var(--border-strong)', checkedBackground: 'var(--accent)', checkedHoverBackground: 'var(--accent)' },
					handle: { background: '#ffffff', hoverBackground: '#ffffff', checkedBackground: '#ffffff', checkedHoverBackground: '#ffffff', color: 'var(--text-3)', hoverColor: 'var(--text-2)', checkedColor: 'var(--accent)', checkedHoverColor: 'var(--accent)' },
				},
			},
		},
		tooltip: {
			root: { maxWidth: '12.5rem', gutter: '4px', shadow: 'var(--shadow)', padding: '4px 8px', borderRadius: '6px' },
			colorScheme: { light: { root: { background: 'var(--elevated)', color: 'var(--text)' } }, dark: { root: { background: 'var(--elevated)', color: 'var(--text)' } } },
		},
		dialog: {
			header: { padding: '20px', gap: '8px' },
			title: { fontSize: '20px', fontWeight: '600' },
			content: { padding: '0 20px 20px 20px' },
			footer: { padding: '0 20px 20px 20px', gap: '8px' },
		},
		drawer: { title: { fontSize: '16px', fontWeight: '600' } },
		card: { root: { background: 'var(--card)', borderRadius: '12px', shadow: 'none' }, body: { padding: '16px', gap: '8px' }, title: { fontSize: '16px', fontWeight: '600' } },
		progressbar: { root: { background: 'var(--well)', borderRadius: '9999px', height: '10px' }, value: { background: 'var(--accent)' }, label: { color: 'var(--on-accent)', fontSize: '11px', fontWeight: '600' } },
		paginator: { navButton: { width: '32px', height: '32px', borderRadius: '8px' } },
		tabs: {
			tab: { padding: '0 10px', fontWeight: '500', borderWidth: '0 0 2px 0', margin: '0 0 -1px 0', gap: '6px', activeColor: 'var(--text)', color: 'var(--text-3)', hoverColor: 'var(--text-2)', activeBorderColor: 'var(--accent)', borderColor: 'transparent', hoverBorderColor: 'transparent' },
			tablist: { background: 'transparent', borderColor: 'var(--border)' },
			tabpanel: { background: 'transparent', padding: '16px 0 0 0' },
			activeBar: { height: '2px', background: 'var(--accent)' },
		},
		checkbox: { root: { borderRadius: '4px', width: '16px', height: '16px' }, icon: { size: '12px' } },
		radiobutton: { root: { width: '16px', height: '16px' }, icon: { size: '8px' } },
		inputnumber: { button: { width: '32px' } },
		datatable: {
			headerCell: { padding: '8px 12px' },
			bodyCell: { padding: '8px 12px' },
			columnTitle: { fontWeight: '600' },
			colorScheme: {
				light: { root: { borderColor: 'var(--border)' }, row: { stripedBackground: 'var(--card-hover)' }, bodyCell: { selectedBorderColor: 'var(--accent-soft)' } },
				dark: { root: { borderColor: 'var(--border)' }, row: { stripedBackground: 'var(--card-hover)' }, bodyCell: { selectedBorderColor: 'var(--accent-soft)' } },
			},
		},
	},
})
